import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Typography, 
    Button, 
    Form, 
    Input, 
    Switch, 
    Divider, 
    Space, 
    App,
    Alert,
    Tabs,
    Upload,
    Image,
    Modal,
    Progress,
    message
} from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    EyeOutlined,
    UploadOutlined,
    InboxOutlined,
    SaveOutlined
} from '@ant-design/icons';
import { BannerImageUpload, HeroSliderUpload } from '../../UI/ImageUpload';
import { resolveImageSrc } from '../../../utils/images';
import api from '../../../services/api';
import { useHomepageContent } from '../../../hooks/useDynamicContent';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
// const { TabPane } = Tabs; // Deprecated - using items prop instead
const { Dragger } = Upload;

function HomepageContent() {
    const { message: appMessage } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form] = Form.useForm();
    const [bannerForm] = Form.useForm();
    const [sliderForm] = Form.useForm();
    
    const { 
        homepageContent, 
        contentLoading, 
        refetch: refetchContent 
    } = useHomepageContent();

    const [banners, setBanners] = useState([]);
    const [heroSliders, setHeroSliders] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    useEffect(() => {
        loadBanners();
        loadHeroSliders();
    }, []);

    const loadBanners = async () => {
        try {
            const response = await api.adminGetMedia({ category: 'banner' });
            console.log('Banners response:', response);
            
            // Handle paginated response structure
            if (response.data && response.data.data) {
                setBanners(response.data.data);
            } else if (Array.isArray(response.data)) {
                setBanners(response.data);
            } else {
                setBanners([]);
            }
        } catch (error) {
            console.error('Failed to load banners:', error);
            console.log('Setting empty banners array due to error');
            setBanners([]);
        }
    };

    const loadHeroSliders = async () => {
        try {
            const response = await api.adminGetMedia({ category: 'hero-slider' });
            console.log('Hero sliders response:', response);
            
            // Handle paginated response structure
            if (response.data && response.data.data) {
                setHeroSliders(response.data.data);
            } else if (Array.isArray(response.data)) {
                setHeroSliders(response.data);
            } else {
                setHeroSliders([]);
            }
        } catch (error) {
            console.error('Failed to load hero sliders:', error);
            console.log('Setting empty hero sliders array due to error');
            setHeroSliders([]);
        }
    };

    const handleContentUpdate = async (values) => {
        try {
            setSaving(true);
            
            const contentUpdates = Object.entries(values).map(([key, value]) => ({
                key,
                content: value,
                type: 'text',
                section: 'homepage'
            }));

            await api.adminBulkUpdateContent({ content: contentUpdates });
            message.success('Homepage content updated successfully!');
            refetchContent();
        } catch (error) {
            setError('Failed to update homepage content');
            message.error('Failed to update homepage content');
        } finally {
            setSaving(false);
        }
    };

    const handleBannerUpload = async (values) => {
        try {
            setSaving(true);
            
            console.log('Banner upload values:', values);
            console.log('Banners array:', values.banners);
            console.log('Banners state:', banners);
            
            if (values.banners && values.banners.length > 0) {
                // Process banner uploads
                for (const banner of values.banners) {
                    console.log('Processing banner:', banner);
                    
                    if (!banner.originFileObj) {
                        console.error('No originFileObj in banner:', banner);
                        throw new Error('File object not found');
                    }
                    
                    console.log('Uploading banner:', banner);
                    console.log('Banner originFileObj:', banner.originFileObj);
                    
                    // Use originFileObj if available, otherwise use the file itself
                    let fileToUpload = banner.originFileObj || banner;
                    
                    // If the file is not a File object, try to extract it
                    if (!(fileToUpload instanceof File)) {
                        console.warn('File is not a File object, attempting to fix:', fileToUpload);
                        // Try to get the actual file from the banner object
                        if (banner instanceof File) {
                            fileToUpload = banner;
                        } else if (banner.file && banner.file instanceof File) {
                            fileToUpload = banner.file;
                        } else {
                            console.error('Cannot extract file from banner:', banner);
                            message.error('Invalid file - please try uploading again');
                            continue;
                        }
                    }
                    
                    console.log('Banner file details:', {
                        banner,
                        originFileObj: banner.originFileObj,
                        fileToUpload,
                        fileType: typeof fileToUpload,
                        isFile: fileToUpload instanceof File,
                        hasName: fileToUpload?.name,
                        hasSize: fileToUpload?.size,
                    });
                    
                    if (!fileToUpload) {
                        console.error('No file found for banner:', banner);
                        message.error('Invalid file - please try uploading again');
                        continue;
                    }
                    
                    if (!(fileToUpload instanceof File)) {
                        console.error('File is not a File object:', fileToUpload);
                        message.error('Invalid file type - please try uploading again');
                        continue;
                    }
                    
                    // Additional validation
                    if (fileToUpload.size === 0) {
                        console.error('File size is 0:', fileToUpload);
                        message.error('File appears to be empty - please try uploading again');
                        continue;
                    }
                    
                    console.log('File validation passed:', {
                        name: fileToUpload.name,
                        size: fileToUpload.size,
                        type: fileToUpload.type,
                        lastModified: fileToUpload.lastModified
                    });
                    
                    const formData = new FormData();
                    formData.append('file', fileToUpload);
                    formData.append('category', 'banner');
                    formData.append('alt_text', banner.name || 'Homepage Banner');
                    formData.append('description', 'Homepage banner image');
                    formData.append('is_public', '1'); // Send as '1' for true, '0' for false

                    console.log('FormData entries:');
                    for (let [key, value] of formData.entries()) {
                        console.log(key, value);
                    }
                    
                    // Verify the file is actually in FormData
                    const fileFromFormData = formData.get('file');
                    console.log('File from FormData:', fileFromFormData);
                    console.log('Is file in FormData a File?', fileFromFormData instanceof File);
                    console.log('File size:', fileFromFormData?.size);
                    console.log('File name:', fileFromFormData?.name);
                    console.log('File type:', fileFromFormData?.type);

                    console.log('Sending banner upload request...');
                    
                    // Test upload first
                    try {
                        const testResult = await api.adminTestUpload(formData);
                        console.log('Test upload result:', testResult);
                    } catch (testError) {
                        console.error('Test upload failed:', testError);
                    }
                    
                    const result = await api.adminUploadMedia(formData);
                    console.log('Banner upload result:', result);
                }
                
                message.success('Banners uploaded successfully!');
                loadBanners();
                bannerForm.resetFields();
            }
        } catch (error) {
            console.error('Banner upload error:', error);
            const errorMessage = error.message || 'Failed to upload banners';
            setError(errorMessage);
            message.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleSliderUpload = async (values) => {
        try {
            setSaving(true);
            
            console.log('Slider upload values:', values);
            console.log('Sliders array:', values.sliders);
            console.log('Sliders state:', heroSliders);
            
            if (values.sliders && values.sliders.length > 0) {
                // Process slider uploads
                for (const slider of values.sliders) {
                    console.log('Processing slider:', slider);
                    
                    if (!slider.originFileObj) {
                        console.error('No originFileObj in slider:', slider);
                        throw new Error('File object not found');
                    }
                    
                    console.log('Uploading slider:', slider);
                    console.log('Slider originFileObj:', slider.originFileObj);
                    
                    // Use originFileObj if available, otherwise use the file itself
                    let fileToUpload = slider.originFileObj || slider;
                    
                    // If the file is not a File object, try to extract it
                    if (!(fileToUpload instanceof File)) {
                        console.warn('File is not a File object, attempting to fix:', fileToUpload);
                        // Try to get the actual file from the slider object
                        if (slider instanceof File) {
                            fileToUpload = slider;
                        } else if (slider.file && slider.file instanceof File) {
                            fileToUpload = slider.file;
                        } else {
                            console.error('Cannot extract file from slider:', slider);
                            message.error('Invalid file - please try uploading again');
                            continue;
                        }
                    }
                    
                    console.log('Slider file details:', {
                        slider,
                        originFileObj: slider.originFileObj,
                        fileToUpload,
                        fileType: typeof fileToUpload,
                        isFile: fileToUpload instanceof File,
                        hasName: fileToUpload?.name,
                        hasSize: fileToUpload?.size,
                    });
                    
                    if (!fileToUpload) {
                        console.error('No file found for slider:', slider);
                        message.error('Invalid file - please try uploading again');
                        continue;
                    }
                    
                    if (!(fileToUpload instanceof File)) {
                        console.error('File is not a File object:', fileToUpload);
                        message.error('Invalid file type - please try uploading again');
                        continue;
                    }
                    
                    // Additional validation
                    if (fileToUpload.size === 0) {
                        console.error('File size is 0:', fileToUpload);
                        message.error('File appears to be empty - please try uploading again');
                        continue;
                    }
                    
                    console.log('File validation passed:', {
                        name: fileToUpload.name,
                        size: fileToUpload.size,
                        type: fileToUpload.type,
                        lastModified: fileToUpload.lastModified
                    });
                    
                    const formData = new FormData();
                    formData.append('file', fileToUpload);
                    formData.append('category', 'hero-slider');
                    formData.append('alt_text', slider.name || 'Hero Slider Image');
                    formData.append('description', 'Hero slider image');
                    formData.append('is_public', '1'); // Send as '1' for true, '0' for false

                    console.log('FormData entries:');
                    for (let [key, value] of formData.entries()) {
                        console.log(key, value);
                    }
                    
                    // Verify the file is actually in FormData
                    const fileFromFormData = formData.get('file');
                    console.log('File from FormData:', fileFromFormData);
                    console.log('Is file in FormData a File?', fileFromFormData instanceof File);
                    console.log('File size:', fileFromFormData?.size);
                    console.log('File name:', fileFromFormData?.name);
                    console.log('File type:', fileFromFormData?.type);

                    console.log('Sending slider upload request...');
                    const result = await api.adminUploadMedia(formData);
                    console.log('Slider upload result:', result);
                }
                
                message.success('Hero slider images uploaded successfully!');
                loadHeroSliders();
                sliderForm.resetFields();
            }
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.message || 'Failed to upload hero slider images';
            setError(errorMessage);
            message.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const saveSlideCTA = async (slideId, payload) => {
        try {
            await api.adminCreateContent({
                key: `hero_slider_cta_${slideId}`,
                type: 'json',
                content: JSON.stringify(payload),
                section: 'homepage',
                is_active: true,
            });
            message.success('CTA saved');
        } catch (e) {
            message.error('Failed to save CTA');
        }
    };

    const deleteBanner = async (id) => {
        try {
            await api.adminDeleteMedia(id);
            message.success('Banner deleted successfully!');
            loadBanners();
        } catch (error) {
            message.error('Failed to delete banner');
        }
    };

    const deleteSlider = async (id) => {
        try {
            await api.adminDeleteMedia(id);
            message.success('Hero slider image deleted successfully!');
            loadHeroSliders();
        } catch (error) {
            message.error('Failed to delete hero slider image');
        }
    };

    const handlePreview = (imageUrl) => {
        setPreviewImage(imageUrl);
        setPreviewVisible(true);
    };

    const bannerUploadProps = {
        name: 'banners',
        multiple: true,
        accept: 'image/*',
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
        },
        beforeUpload: (file) => {
            // Check file size
            const isLt30M = file.size / 1024 / 1024 < 30;
            if (!isLt30M) {
                message.error(`Image must be smaller than 30MB! Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                return Upload.LIST_IGNORE; // Remove the file from the list
            }
            
            // Check file type
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                message.error('You can only upload image files!');
                return Upload.LIST_IGNORE; // Remove the file from the list
            }
            
            // Return true to allow file to be added to the list, but prevent auto upload
            return false; // Prevent auto upload but allow file to be in the list
        },
        onChange: (info) => {
            console.log('Banner upload onChange:', info);
            console.log('File list:', info.fileList);
            
            // Process each file in the list
            const processedFileList = info.fileList.map(fileItem => {
                // Ensure the file has originFileObj
                if (fileItem.originFileObj) {
                    return fileItem;
                } else if (fileItem instanceof File) {
                    return {
                        ...fileItem,
                        originFileObj: fileItem,
                        uid: fileItem.uid || Date.now() + Math.random(),
                        name: fileItem.name,
                        status: 'done'
                    };
                } else {
                    console.warn('Invalid file item:', fileItem);
                    return fileItem;
                }
            });
            
            console.log('Processed file list:', processedFileList);
            // Update the banners state with the processed file list
            setBanners(processedFileList);
        },
        listType: 'picture-card',
        maxCount: 5,
    };

    const sliderUploadProps = {
        name: 'sliders',
        multiple: true,
        accept: 'image/*',
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
        },
        beforeUpload: (file) => {
            // Check file size
            const isLt30M = file.size / 1024 / 1024 < 30;
            if (!isLt30M) {
                message.error(`Image must be smaller than 30MB! Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                return Upload.LIST_IGNORE; // Remove the file from the list
            }
            
            // Check file type
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                message.error('You can only upload image files!');
                return Upload.LIST_IGNORE; // Remove the file from the list
            }
            
            // Return true to allow file to be added to the list, but prevent auto upload
            return false; // Prevent auto upload but allow file to be in the list
        },
        onChange: (info) => {
            console.log('Slider upload onChange:', info);
            console.log('File list:', info.fileList);
            
            // Process each file in the list
            const processedFileList = info.fileList.map(fileItem => {
                // Ensure the file has originFileObj
                if (fileItem.originFileObj) {
                    return fileItem;
                } else if (fileItem instanceof File) {
                    return {
                        ...fileItem,
                        originFileObj: fileItem,
                        uid: fileItem.uid || Date.now() + Math.random(),
                        name: fileItem.name,
                        status: 'done'
                    };
                } else {
                    console.warn('Invalid file item:', fileItem);
                    return fileItem;
                }
            });
            
            console.log('Processed file list:', processedFileList);
            // Update the sliders state with the processed file list
            setHeroSliders(processedFileList);
        },
        listType: 'picture-card',
        maxCount: 10,
    };

    const normFile = (e) => {
        console.log('normFile called with:', e);
        if (Array.isArray(e)) {
            console.log('Returning array:', e);
            return e;
        }
        const result = e && e.fileList;
        console.log('Returning fileList:', result);
        return result;
    };

    return (
        <App>
            <div>
            <div className="mb-6">
                <Title level={2}>Homepage Content Management</Title>
                <Paragraph type="secondary">
                    Manage homepage text content, banners, and hero slider images.
                </Paragraph>
            </div>

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    className="mb-6"
                    closable
                    onClose={() => setError('')}
                />
            )}

            <Tabs 
                defaultActiveKey="content" 
                type="card"
                items={[
                    {
                        key: 'content',
                        label: 'Text Content',
                        children: (
                    <Card>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleContentUpdate}
                            initialValues={{
                                homepage_hero_title: homepageContent?.homepage_hero_title?.content || '',
                                homepage_hero_subtitle: homepageContent?.homepage_hero_subtitle?.content || 'Discover amazing caterers and create unforgettable experiences',
                                homepage_features_title: homepageContent?.homepage_features_title?.content || 'Why Choose Us',
                                homepage_features_subtitle: homepageContent?.homepage_features_subtitle?.content || 'We make event planning simple and stress-free'
                            }}
                        >
                            <Row gutter={[24, 24]}>
                                <Col span={24}>
                                    <Title level={4}>Hero Section</Title>
                                    <Form.Item
                                        label="Hero Title"
                                        name="homepage_hero_title"
                                        rules={[{ required: true, message: 'Please enter hero title' }]}
                                    >
                                        <Input placeholder="Enter hero title" size="large" />
                                    </Form.Item>
                                    
                                    <Form.Item
                                        label="Hero Subtitle"
                                        name="homepage_hero_subtitle"
                                        rules={[{ required: true, message: 'Please enter hero subtitle' }]}
                                    >
                                        <TextArea 
                                            placeholder="Enter hero subtitle" 
                                            rows={3}
                                            showCount
                                            maxLength={200}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={24}>
                                    <Title level={4}>Features Section</Title>
                                    <Form.Item
                                        label="Features Title"
                                        name="homepage_features_title"
                                        rules={[{ required: true, message: 'Please enter features title' }]}
                                    >
                                        <Input placeholder="Enter features title" />
                                    </Form.Item>
                                    
                                    <Form.Item
                                        label="Features Subtitle"
                                        name="homepage_features_subtitle"
                                        rules={[{ required: true, message: 'Please enter features subtitle' }]}
                                    >
                                        <TextArea 
                                            placeholder="Enter features subtitle" 
                                            rows={2}
                                            showCount
                                            maxLength={150}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <div className="flex justify-end">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={saving}
                                    icon={<SaveOutlined />}
                                    size="large"
                                >
                                    Update Content
                                </Button>
                            </div>
                        </Form>
                    </Card>
                        )
                    },
                        {
                            key: 'controls',
                            label: 'Content Controls',
                            children: (
                    <Card>
                        <Form
                            layout="vertical"
                            onFinish={handleContentUpdate}
                            initialValues={{
                                homepage_cta_title: homepageContent?.homepage_cta_title?.content || '',
                                homepage_cta_button_label: homepageContent?.homepage_cta_button_label?.content || 'Get Quote',
                                homepage_cta_url: homepageContent?.homepage_cta_url?.content || '/booking',
                                homepage_seo_title: homepageContent?.homepage_seo_title?.content || 'Spoon & Spice – Catering, Menus, Vendors',
                                homepage_seo_description: homepageContent?.homepage_seo_description?.content || 'Discover caterers, browse menus, and plan your perfect event.',
                                homepage_seo_keywords: homepageContent?.homepage_seo_keywords?.content || 'catering, menus, wedding, events, vendors'
                            }}
                        >
                            <Title level={4}>Call To Action</Title>
                            <Row gutter={[16,16]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="homepage_cta_title"
                                        label="CTA Title"
                                        rules={[{ required: true, message: 'Please enter CTA title' }]}
                                    >
                                        <Input placeholder="e.g., Plan your event with us" />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="homepage_cta_button_label"
                                        label="CTA Button Label"
                                        rules={[{ required: true, message: 'Please enter button label' }]}
                                    >
                                        <Input placeholder="e.g., Get Quote" />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="homepage_cta_url"
                                        label="CTA URL"
                                        rules={[{ required: true, message: 'Please enter CTA URL' }]}
                                    >
                                        <Input placeholder="e.g., /booking" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title level={4}>SEO</Title>
                            <Row gutter={[16,16]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="homepage_seo_title"
                                        label="SEO Title"
                                        rules={[{ required: true, message: 'Please enter SEO title' }]}
                                    >
                                        <Input placeholder="Homepage SEO title" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="homepage_seo_keywords"
                                        label="SEO Keywords (comma separated)"
                                    >
                                        <Input placeholder="catering, menus, wedding, events, vendors" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="homepage_seo_description"
                                        label="SEO Description"
                                        rules={[{ required: true, message: 'Please enter SEO description' }]}
                                    >
                                        <TextArea rows={3} maxLength={300} showCount placeholder="Short description for search engines" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <div className="flex justify-end">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={saving}
                                    icon={<SaveOutlined />}
                                >
                                    Save Controls
                                </Button>
                            </div>
                        </Form>
                    </Card>
                            )
                        },
                    {
                        key: 'banners',
                        label: 'Banners',
                        children: (
                    <Card>
                        <div className="mb-6">
                            <Title level={4}>Upload New Banners</Title>
                            <div className="mb-4">
                                <Button 
                                    type="primary" 
                                    onClick={() => {
                                        console.log('Current banners state:', banners);
                                        console.log('Banner form values:', bannerForm.getFieldsValue());
                                    }}
                                >
                                    Debug Banners State
                                </Button>
                                <div className="mt-4 p-4 border border-dashed border-gray-300 rounded">
                                    <h5>Simple File Upload Test</h5>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            
                                            console.log('Simple upload file:', file);
                                            console.log('File details:', {
                                                name: file.name,
                                                size: file.size,
                                                type: file.type,
                                                lastModified: file.lastModified
                                            });
                                            
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            formData.append('category', 'test');
                                            formData.append('alt_text', file.name);
                                            formData.append('description', 'Simple test upload');
                                            formData.append('is_public', '1');
                                            
                                            console.log('Simple test FormData entries:');
                                            for (let [key, value] of formData.entries()) {
                                                console.log(key, value);
                                            }
                                            
                                            try {
                                                const result = await api.adminTestUpload(formData);
                                                console.log('Simple test upload result:', result);
                                                message.success('Simple test upload successful!');
                                            } catch (error) {
                                                console.error('Simple test upload error:', error);
                                                message.error('Simple test upload failed: ' + error.message);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <Form form={bannerForm} onFinish={handleBannerUpload}>
                                <Form.Item 
                                    name="banners"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    rules={[{ required: true, message: 'Please upload at least one banner' }]}
                                >
                                    <Dragger {...bannerUploadProps}>
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined />
                                        </p>
                                        <p className="ant-upload-text">Click or drag banner images to this area to upload</p>
                                        <p className="ant-upload-hint">
                                            Support for multiple image upload. Recommended size: 1920x600px. Max file size: 2MB
                                        </p>
                                    </Dragger>
                                </Form.Item>
                                
                                <div className="flex justify-end">
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        loading={saving}
                                        icon={<UploadOutlined />}
                                    >
                                        Upload Banners
                                    </Button>
                                </div>
                            </Form>
                        </div>

                        <Divider />

                        <div>
                            <Title level={4}>Current Banners</Title>
                            <Row gutter={[16, 16]}>
                                {banners.map((banner) => (
                                    <Col key={banner.id} xs={24} sm={12} md={8} lg={6}>
                                        <Card
                                            hoverable
                                            cover={
                                                <Image
                                                    src={resolveImageSrc(banner.url || banner.file_path)}
                                                    alt={banner.alt_text}
                                                    height={150}
                                                    className="object-cover"
                                                />
                                            }
                                            actions={[
                                                <Button 
                                                    type="text" 
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handlePreview(resolveImageSrc(banner.url || banner.file_path))}
                                                />,
                                                <Button 
                                                    type="text" 
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => deleteBanner(banner.id)}
                                                />
                                            ]}
                                        >
                                            <Card.Meta
                                                title={banner.alt_text}
                                                description={`${(banner.file_size / 1024 / 1024).toFixed(2)} MB`}
                                            />
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Card>
                        )
                    },
                    {
                        key: 'slider',
                        label: 'Hero Slider',
                        children: (
                    <Card>
                        <div className="mb-6">
                            <Title level={4}>Upload Hero Slider Images</Title>
                            <div className="mb-4">
                                <Button 
                                    type="primary" 
                                    onClick={() => {
                                        console.log('Current sliders state:', heroSliders);
                                        console.log('Slider form values:', sliderForm.getFieldsValue());
                                    }}
                                >
                                    Debug Sliders State
                                </Button>
                            </div>
                            <Form form={sliderForm} onFinish={handleSliderUpload}>
                                <Form.Item 
                                    name="sliders"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    rules={[{ required: true, message: 'Please upload at least one slider image' }]}
                                >
                                    <Dragger {...sliderUploadProps}>
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined />
                                        </p>
                                        <p className="ant-upload-text">Click or drag slider images to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for multiple image upload. Recommended size: 1920x800px. Max file size: 30MB
                        </p>
                                    </Dragger>
                                </Form.Item>
                                
                                <div className="flex justify-end">
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        loading={saving}
                                        icon={<UploadOutlined />}
                                    >
                                        Upload Slider Images
                                    </Button>
                                </div>
                            </Form>
                        </div>

                        <Divider />

                        <div>
                            <Title level={4}>Current Hero Slider Images</Title>
                            <Row gutter={[16, 16]}>
                                {heroSliders.map((slider) => (
                                    <Col key={slider.id} xs={24} sm={12} md={8} lg={6}>
                                        <Card
                                            hoverable
                                            cover={
                                                <Image
                                                    src={resolveImageSrc(slider.url || slider.file_path)}
                                                    alt={slider.alt_text}
                                                    height={150}
                                                    className="object-cover"
                                                />
                                            }
                                            actions={[
                                                <Button 
                                                    type="text" 
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handlePreview(resolveImageSrc(slider.url || slider.file_path))}
                                                />,
                                                <Button 
                                                    type="text" 
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => deleteSlider(slider.id)}
                                                />
                                            ]}
                                        >
                                            <Card.Meta
                                                title={slider.alt_text}
                                                description={`${(slider.file_size / 1024 / 1024).toFixed(2)} MB`}
                                            />
                                            <Divider />
                                            <Form
                                                layout="vertical"
                                                onFinish={(values) => saveSlideCTA(slider.id, values)}
                                            >
                                                <Form.Item label="CTA Title" name="title">
                                                    <Input placeholder="e.g., Book Your Event" />
                                                </Form.Item>
                                                <Form.Item label="CTA Subtitle" name="subtitle">
                                                    <Input placeholder="Short supporting text" />
                                                </Form.Item>
                                                <Form.Item label="Button Label" name="buttonLabel">
                                                    <Input placeholder="e.g., Get Quote" />
                                                </Form.Item>
                                                <Form.Item label="Button URL" name="buttonUrl">
                                                    <Input placeholder="e.g., /booking" />
                                                </Form.Item>
                                                <div className="flex justify-end">
                                                    <Button size="small" type="primary" htmlType="submit" icon={<SaveOutlined />}>Save CTA</Button>
                                                </div>
                                            </Form>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Card>
                        )
                    }
                ]}
            />

            {/* Image Preview Modal */}
            <Modal
                open={previewVisible}
                title="Image Preview"
                footer={null}
                onCancel={() => setPreviewVisible(false)}
                width={800}
            >
                <Image
                    alt="Preview"
                    style={{ width: '100%' }}
                    src={previewImage}
                />
            </Modal>
            </div>
        </App>
    );
}

export default HomepageContent;
